import getInObject from '@insight/toolkit-utils/lib/helpers/getInObject';
import isPromise from '@insight/toolkit-utils/lib/types/basic/isPromise';
import setInObject from '@insight/toolkit-utils/lib/helpers/setInObject';

import { FORM_ERROR } from './constants';
import { filterFieldState, filterFormState } from './filter';
import {
  changeValue,
  hasAnyError,
  hasSyncErrors,
  markAllFieldsTouched,
  notify,
  notifySubscriber,
  publishFieldState,
  publishFormState,
  renameField,
} from './internals';
import { validate } from './validate';

const tripleEquals = (a, b) => a === b;

const createForm = (config) => {
  if (!config) {
    throw new Error('Form expected a config object to be passed');
  }
  const { initialValues, mutators, onSubmit } = config;
  if (!onSubmit) {
    throw new Error('Form expected an onSubmit function to be passed');
  }

  const state = {
    asyncValidationPromises: {},
    config,
    fields: {},
    fieldSubscribers: {},
    formState: {
      dirtySinceLastSubmit: false,
      errors: {},
      initialValues: initialValues && { ...initialValues },
      invalid: false,
      pristine: true,
      submitting: false,
      submitFailed: false,
      submitSucceeded: false,
      valid: true,
      validating: 0,
      values: initialValues ? { ...initialValues } : {},
    },
    inBatch: false,
    lastFormState: undefined,
    nextAsyncValidationKey: 0,
    notifying: false,
    scheduleNotification: false,
    subscribers: { entries: {}, index: 0 },
    validationBlocked: false,
    validationPaused: false,
  };

  const notifyFieldListeners = (name) => {
    const { fields, fieldSubscribers, formState, inBatch } = state;
    if (inBatch) {
      return;
    }
    const safeFields = { ...fields };
    const notifyField = (id) => {
      const field = safeFields[id];
      const fieldState = publishFieldState(formState, field);
      const { lastFieldState } = field;
      field.lastFieldState = fieldState;
      const fieldSubscriber = fieldSubscribers[id];
      if (fieldSubscriber) {
        notify(
          fieldSubscriber,
          fieldState,
          lastFieldState,
          filterFieldState,
          lastFieldState === undefined
        );
      }
    };

    if (name) {
      notifyField(name);
    } else {
      Object.keys(safeFields).forEach(notifyField);
    }
  };

  const notifyFormListeners = () => {
    if (state.notifying) {
      state.scheduleNotification = true;
    } else {
      state.notifying = true;
      // debug
      if (!state.inBatch && !state.validationPaused) {
        const { lastFormState } = state;
        const nextFormState = publishFormState(state);
        if (nextFormState !== lastFormState) {
          state.lastFormState = nextFormState;
          notify(
            state.subscribers,
            nextFormState,
            lastFormState,
            filterFormState
          );
        }
      }
      state.notifying = false;
      if (state.scheduleNotification) {
        state.scheduleNotification = false;
        notifyFormListeners();
      }
    }
  };

  const getMutatorApi =
    (key) =>
    (...args) => {
      if (mutators) {
        const mutableState = {
          fields: state.fields,
          fieldSubscribers: state.fieldSubscribers,
          formState: state.formState,
          lastFormState: state.lastFormState,
        };
        const returnValue = mutators[key](args, mutableState, {
          changeValue,
          renameField,
        });

        state.fields = mutableState.fields;
        state.fieldSubscribers = mutableState.fieldSubscribers;
        state.formState = mutableState.formState;
        state.lastFormState = mutableState.lastFormState;

        validate(state, undefined, () => {
          notifyFieldListeners();
          notifyFormListeners();
        });
      }
    };

  const mutatorsApi = mutators
    ? Object.keys(mutators).reduce(
        (result, key) => ({
          ...result,
          [key]: getMutatorApi(key),
        }),
        {}
      )
    : {};

  const beforeSubmit = () =>
    Object.keys(state.fields).some(
      (name) =>
        state.fields[name].beforeSubmit &&
        state.fields[name].beforeSubmit() === false
    );

  const afterSubmit = () =>
    Object.keys(state.fields).forEach(
      (name) =>
        state.fields[name].afterSubmit && state.fields[name].afterSubmit()
    );

  // get initialErrors
  if (config.skipValidateOnMount) {
    state.formState.errors = {};
  } else {
    validate(state, undefined, () => {
      notifyFormListeners();
    });
  }

  const api = {
    batch: (fn) => {
      state.inBatch = true;
      fn();
      state.inBatch = false;
      notifyFieldListeners();
      notifyFormListeners();
    },

    blur: (name) => {
      const { config, fields, formState } = state;
      const prev = fields[name];
      if (prev) {
        // can only call blur on a registered field
        delete formState.active;
        fields[name] = {
          ...prev,
          active: false,
          touched: true,
        };
        if (config.validateOnBlur && !config.skipValidateOnMount) {
          validate(state, name, () => {
            notifyFieldListeners(name);
            notifyFormListeners();
          });
        } else {
          notifyFieldListeners();
          notifyFormListeners();
        }
      }
    },

    change: (name, value) => {
      const { config, formState } = state;
      if (getInObject(formState.values, name) !== value) {
        changeValue(state, name, () => value);
        if (config.validateOnBlur) {
          validate(state, name, () => {
            notifyFieldListeners(name);
            notifyFormListeners();
          });
        } else {
          notifyFieldListeners();
          notifyFormListeners();
        }
      }
    },

    focus: (name) => {
      const { fields, formState } = state;
      const field = fields[name];
      if (field && !field.active) {
        formState.active = name;
        field.active = true;
        field.visited = true;
        notifyFormListeners();
      }
    },

    getFieldState: (name) => {
      const field = state.fields[name];
      return field && field.lastFieldState;
    },

    getRegisteredFields: () => Object.keys(state.fields),

    getState: () => publishFormState(state),

    initialize: (values) => {
      const { config, fields, formState } = state;
      if (!config.keepDirtyOnReinitialize) {
        formState.values = values;
      }

      Object.keys(fields).forEach((key) => {
        const field = fields[key];
        field.touched = false;
        field.visited = false;
        if (config.keepDirtyOnReinitialize) {
          const pristine = fields[key].isEqual(
            getInObject(formState.values, key),
            getInObject(formState.initialValues || {}, key)
          );
          if (pristine) {
            // only update pristine values
            formState.values = setInObject(
              formState.values,
              key,
              getInObject(values, key)
            );
          }
        }
      });

      formState.initialValues = values;
      if (!config.skipValidateOnMount) {
        validate(state, undefined, () => {
          notifyFieldListeners();
          notifyFormListeners();
        });
      } else {
        notifyFieldListeners();
        notifyFormListeners();
      }
    },

    isValidationPaused: () => state.validationPaused,

    mutators: mutatorsApi,

    pauseValidation: () => {
      state.validationPaused = true;
    },

    registerField: (name, subscriber, subscription = {}, fieldConfig = {}) => {
      if (!state.fieldSubscribers[name]) {
        state.fieldSubscribers[name] = { entries: {}, index: 0 };
      }

      const index = state.fieldSubscribers[name].index++;
      state.fieldSubscribers[name].entries[index] = {
        notified: false,
        subscriber,
        subscription,
      };

      if (!state.fields[name]) {
        state.fields[name] = {
          active: false,
          afterSubmit: fieldConfig.afterSubmit,
          beforeSubmit: fieldConfig.beforeSubmit,
          blur: () => api.blur(name),
          change: (value) => api.change(name, value),
          data: {},
          focus: () => api.focus(name),
          isEqual: fieldConfig.isEqual || tripleEquals,
          lastFieldState: undefined,
          modified: false,
          name,
          touched: false,
          valid: true,
          validateFields: fieldConfig.validateFields,
          validators: {},
          validating: false,
          visited: false,
        };
      }

      const hasValidator = !!(
        fieldConfig.getValidator && fieldConfig.getValidator()
      );

      if (fieldConfig.getValidator) {
        state.fields[name].validators[index] = fieldConfig.getValidator;
      }

      if (fieldConfig.initialValue !== undefined) {
        state.formState.initialValues = setInObject(
          state.formState.initialValues || {},
          name,
          fieldConfig.initialValue
        );
      }

      if (fieldConfig.defaultValue !== undefined) {
        state.formState.values = setInObject(
          state.formState.values,
          name,
          fieldConfig.defaultValue
        );
      }

      if (hasValidator && !fieldConfig.skipValidateOnMount) {
        validate(state, undefined, () => {
          notifyFormListeners();
          notifyFieldListeners();
        });
      } else {
        notifyFormListeners();
        notifyFieldListeners(name);
      }

      return () => {
        let validatorRemoved = false;
        if (state.fields[name]) {
          // state.fields[name] may have been removed by a mutator
          validatorRemoved = !!(
            state.fields[name].validators[index] &&
            state.fields[name].validators[index]()
          );
          delete state.fields[name].validators[index];
        }

        delete state.fieldSubscribers[name].entries[index];

        const isLastSubscriber = !Object.keys(
          state.fieldSubscribers[name].entries
        ).length;
        if (isLastSubscriber) {
          delete state.fieldSubscribers[name];
          delete state.fields[name];
          if (validatorRemoved) {
            state.formState.errors =
              setInObject(state.formState.errors, name, undefined) || {};
          }
        }
        if (
          state.config.destroyOnUnregister ||
          fieldConfig.destroyOnUnregister
        ) {
          state.formState.values =
            setInObject(state.formState.values, name, undefined, true) || {};
        }

        if (validatorRemoved) {
          validate(state, undefined, () => {
            notifyFormListeners();
            notifyFieldListeners();
          });
        } else if (isLastSubscriber) {
          // values or errors may have changed
          notifyFormListeners();
        }
      };
    },

    reset: (initialValues = state.formState.initialValues) => {
      state.formState.submitFailed = false;
      state.formState.submitSucceeded = false;
      delete state.formState.submitError;
      delete state.formState.submitErrors;
      delete state.formState.lastSubmittedValues;
      api.initialize(initialValues || {});
    },

    resetFieldState: (name) => {
      state.fields[name] = {
        ...state.fields[name],
        ...{
          active: false,
          lastFieldState: undefined,
          modified: false,
          touched: true,
          valid: true,
          validating: false,
          visited: false,
        },
      };
      validate(undeined, () => {
        notifyFieldListeners();
        notifyFormListeners();
      });
    },

    resumeValidation: () => {
      state.validationPaused = false;
      if (state.validationBlocked) {
        // validation was attempted while it was paused, so run it now
        validate(state, undefined, () => {
          notifyFieldListeners();
          notifyFormListeners();
        });
      }
      state.validationBlocked = false;
    },

    setConfig: (name, value) => {
      switch (name) {
        case 'destroyOnUnregister':
          config.destroyOnUnregister = value;
          break;
        case 'initialValues':
          api.initialize(value);
          break;
        case 'keepDirtyOnReinitialize':
          config.keepDirtyOnReinitialize = value;
          break;
        case 'mutators':
          config.mutators = value;
          if (value) {
            Object.keys(mutatorsApi).forEach((key) => {
              if (!(key in value)) {
                delete mutatorsApi[key];
              }
            });
            Object.keys(value).forEach((key) => {
              mutatorsApi[key] = getMutatorApi(key);
            });
          } else {
            Object.keys(mutatorsApi).forEach((key) => {
              delete mutatorsApi[key];
            });
          }
          break;
        case 'onSubmit':
          config.onSubmit = value;
          break;
        case 'validate':
          config.validate = value;
          validate(state, undefined, () => {
            notifyFieldListeners();
            notifyFormListeners();
          });
          break;
        case 'validateOnBlur':
          config.validateOnBlur = value;
          break;
        default:
          throw new Error(
            `createForm - setConfig. Unrecognized config option ${name}`
          );
      }
    },

    submit: () => {
      const {
        asyncValidationPromises,
        formState,
        config: { skipValidateOnMount },
      } = state;

      if (skipValidateOnMount) {
        validate(state, undefined, () => {
          notifyFieldListeners();
          notifyFormListeners();
        });
      }

      if (hasSyncErrors(formState)) {
        markAllFieldsTouched(state);
        formState.submitFailed = true;
        notifyFormListeners();
        notifyFieldListeners();
        return;
      }

      const asyncValidationKeys = Object.keys(asyncValidationPromises);
      if (asyncValidationKeys.length) {
        // Still waiting for validation to complete
        Promise.all(
          asyncValidationKeys.map((key) => asyncValidationKeys[Number(key)])
        ).then(api.submit, api.submit);
        return;
      }

      const isSubmissionBlocked = beforeSubmit();
      if (isSubmissionBlocked) {
        return;
      }

      let completeCalled = false;
      let resolvePromise;

      const complete = (errors) => {
        formState.submitting = false;
        if (errors && hasAnyError(errors)) {
          formState.submitFailed = true;
          formState.submitSucceeded = false;
          formState.submitErrors = errors;
          formState.submitError = errors[FORM_ERROR];
          markAllFieldsTouched(state);
        } else {
          formState.submitFailed = false;
          formState.submitSucceeded = true;
          afterSubmit();
        }

        notifyFormListeners();
        notifyFieldListeners();
        completeCalled = true;

        if (resolvePromise) {
          resolvePromise(errors);
        }

        return errors;
      };

      delete formState.submitErrors;
      delete formState.submitError;
      formState.submitting = true;
      formState.submitFailed = false;
      formState.submitSucceeded = false;
      formState.lastSubmittedValues = { ...formState.values };

      // onSubmit is either sync, callback, or async with a promise
      const result = onSubmit(formState.values, api, complete);
      if (!completeCalled) {
        if (result && isPromise(result)) {
          notifyFormListeners();
          notifyFieldListeners();
          return result.then(complete, (err) => {
            complete();
            throw err;
          });
        }
        if (onSubmit.length >= 3) {
          notifyFormListeners();
          notifyFieldListeners();
          return new Promise((resolve) => {
            resolvePromise = resolve;
          });
        }
        complete(result);
      }
    },

    subscribe: (subscriber, subscription) => {
      if (!subscription) {
        throw new Error(
          'No subscription was provided to form. What values do you want to listen to?'
        );
      }
      if (!subscriber) {
        throw new Error('No subscriber callback passed to form');
      }

      const { lastFormState, subscribers } = state;
      const index = subscribers.index++;
      subscribers.entries[index] = {
        subscriber,
        subscription,
      };

      const nextFormState = publishFormState(state);
      if (nextFormState !== lastFormState) {
        state.lastFormState = nextFormState;
      }
      notifySubscriber(
        subscriber,
        subscription,
        nextFormState,
        nextFormState,
        filterFormState,
        true
      );

      return () => {
        delete subscribers.entries[index];
      };
    },
  };

  return api;
};

export default createForm;
